/**
 * The follow-organizer WIRE — verified against the eb-ui oracle
 * `eb-ui/packages/attendee/follow-organizer-ebui/src/api/organizer.js` and the
 * `memory.md` WIRE-CORRECTNESS AUDIT.
 *
 * follow  WRITE  = POST /api/v3/destination/organizers/{id}/follow/    (id in PATH, no body)
 * unfollow WRITE = POST /api/v3/destination/organizers/{id}/unfollow/  (id in PATH, no body, POST not DELETE)
 * status  READ   = GET  /api/v3/organizers/?ids={ids}&expand.organizer=follow_status
 *
 * NB the `/users/me/following/organizers/` path recorded in an earlier run was
 * FABRICATED (exists in neither eb-ui nor eb-web) — it is NOT reintroduced here.
 *
 * These are the CORE paths. A Valkyrie consumer never calls Core directly from
 * the client: it exposes a same-origin BFF (`src/pages/api/**`) whose handlers
 * hit these Core paths server-side (forwarding cookie + eb-soa-token +
 * X-CSRFToken on the POSTs). `buildFollowPath` / `buildUnfollowPath` /
 * `buildOrganizersStatusPath` therefore produce the paths RELATIVE to a
 * configurable BFF base (default `/api/v3` to mirror the Core shape 1:1), and
 * the client fetches them same-origin. See `client.ts` + `MEMORY-DELTA.md`.
 */

/** Default base the client prepends. Mirrors Core's `/api/v3` so a BFF that
 *  transparently proxies keeps identical paths. A consumer whose BFF is mounted
 *  elsewhere (e.g. `<basePath>/api`) overrides this via client config. */
export const DEFAULT_API_BASE = '/api/v3';

/**
 * SECURITY (off-origin credentialed fetch): `defaultFetcher` always sends
 * `credentials: 'include'`, so the session cookie rides along. If a caller could
 * set `apiBase` to a protocol-relative (`//evil.com`), absolute
 * (`https://evil.com`), or backslash-obfuscated value, the `build*Path` helpers
 * would produce a cross-origin URL and leak the viewer's cookie off-origin.
 *
 * Constrain `base` to a SAME-ORIGIN path prefix: it must be a single leading
 * slash followed by a non-slash, non-backslash character (so `/api/v3`,
 * `/manage/api` are fine, but `//host`, `/\host`, `http(s)://…`, `\\host`, and
 * scheme-relative forms are rejected). Anything else falls back to the safe
 * default base.
 *
 * Mirrors `modules/app-discovery/src/api/appsClient.ts` (`isSameOriginBase`).
 */
export const isSameOriginBase = (base: unknown): base is string => {
    if (typeof base !== 'string' || base.length === 0) {
        return false;
    }
    // Reject any backslashes outright (browsers treat `\` like `/` in URLs, so
    // `/\evil.com` or `\\evil.com` can become protocol-relative/authority).
    if (base.includes('\\')) {
        return false;
    }
    // Must be a rooted path: exactly one leading slash, and the next char must
    // not be another slash (which would make it protocol-relative `//host`).
    if (base[0] !== '/' || base[1] === '/') {
        return false;
    }
    return true;
};

/**
 * Normalize a configured base to a safe same-origin path prefix, falling back to
 * {@link DEFAULT_API_BASE} for anything off-origin or malformed. Trailing
 * slashes are stripped so concatenation with a leading-slash path never doubles
 * up.
 */
export const normalizeBase = (base: unknown): string => {
    if (!isSameOriginBase(base)) {
        return DEFAULT_API_BASE;
    }
    return base.replace(/\/+$/, '');
};

export const buildFollowPath = (organizerId: string, base = DEFAULT_API_BASE) =>
    `${normalizeBase(base)}/destination/organizers/${organizerId}/follow/`;

export const buildUnfollowPath = (
    organizerId: string,
    base = DEFAULT_API_BASE,
) =>
    `${normalizeBase(base)}/destination/organizers/${organizerId}/unfollow/`;

/**
 * Follow-status READ. `ids` is comma-joined (Core accepts a repeated/joined
 * `ids` query param); `expand.organizer=follow_status` returns the
 * `follow_status.{followed_by_you,num_followers}` block per organizer.
 */
export const buildOrganizersStatusPath = (
    organizerIds: string[],
    base = DEFAULT_API_BASE,
) => {
    const params = new URLSearchParams({
        ids: organizerIds.join(','),
        'expand.organizer': 'follow_status',
    });
    return `${normalizeBase(base)}/organizers/?${params.toString()}`;
};
